import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Alert, Avatar, Box, Button, CircularProgress, IconButton, InputAdornment, Paper, Skeleton, Snackbar, TextField, Typography } from "@mui/material";
import { colors } from "../theme/muiColors";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import { getMyCredentials, storeMyCredentials, updateMyCredentials } from "../utils";

// Official PayPal logo from PayPal Logo Center (paypalobjects.com)
const PAYPAL_LOGO_URL = "https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg";

export const PaypalCredentials = ({ sx }) => {
  const [initialClientId, setInitialClientId] = useState("");
  const [initialClientSecret, setInitialClientSecret] = useState("");
  const [clientId, setClientId] = useState(undefined);
  const [clientSecret, setClientSecret] = useState(undefined);
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingClientId, setIsEditingClientId] = useState(false);
  const [isEditingClientSecret, setIsEditingClientSecret] = useState(false);
  const token = sessionStorage.getItem("token");
  const [resSeverity, setResSeverity] = useState("success");
  const [resSeverityMessage, setResSeverityMessage] = useState("");
  const [isNewCredentials, setIsNewCredentials] = useState(false);

  const handleIntegrate = () => {
    setIsSubmitting(true);
    console.log("Client ID:", clientId);
    console.log("Client Secret:", clientSecret);
    const cbManagePaypalCredentials = isNewCredentials ? storeMyCredentials : updateMyCredentials;

    cbManagePaypalCredentials(token, clientId, clientSecret)
      .then((res) => {
        console.log(res);
        if (res.status === 201) {
          setResSeverity("success");
          setResSeverityMessage("PayPal credentials successfully integrated. Your account is now ready to process payments.");
        } else if (res.status === 200) {
          setResSeverity("info");
          setResSeverityMessage("PayPal credentials successfully updated.");
        }
        setIsNewCredentials(false);
      })
      .catch((error) => {
        if (!isAxiosError(error)) {
          setResSeverity("error");
          setResSeverityMessage("Internal Server Error");
          setClientId(initialClientId);
          setClientSecret(initialClientSecret);
          return;
        }
        const { status } = error.response;
        switch (status) {
          case 400:
            setResSeverity("warning");
            break;
          case 401:
            setResSeverity("warning");
            break;
          default:
            setResSeverity("error");
            break;
        }
        setResSeverityMessage(error.response.data?.message);
        setClientId(initialClientId);
        setClientSecret(initialClientSecret);
      })
      .finally(() => {
        setOpenSnackbar(true);
        setIsSubmitting(false);
      });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  useEffect(() => {
    getMyCredentials(token)
      .then((res) => {
        setIsNewCredentials(false);
        setClientId(res.clientId);
        setClientSecret(res.clientSecret);
        setInitialClientId(res.clientId);
        setInitialClientSecret(res.clientSecret);
        setLoading(false);
      })
      .catch((error) => {
        if (isAxiosError(error) && error.response.status === 404) {
          setClientId(undefined);
          setClientSecret(undefined);
          setIsNewCredentials(true);
          return;
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Paper
      sx={{
        width: "600px",
        height: "auto",
        pt: 15,
        pb: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -10,
          left: -15,
        }}
      >
        <Avatar
          alt="PayPal Logo"
          src={PAYPAL_LOGO_URL}
          sx={{
            width: 140,
            height: 140,
            border: "2px solid #244499",
          }}
        />
      </Box>

      <Typography variant="h1" fontSize="36px" color="#244499" sx={{ position: "absolute", top: 20 }}>
        PayPal Integrations
      </Typography>

      {loading ? (
        <>
          <Skeleton variant="rectangular" width="80%" height={56} sx={{ marginBottom: 2 }} animation="wave" />
          <Skeleton variant="rectangular" width="80%" height={56} sx={{ marginBottom: 2 }} animation="wave" />
        </>
      ) : (
        <>
          <TextField
            label="Client ID"
            variant="outlined"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            onFocus={() => setIsEditingClientId(true)}
            onBlur={() => setIsEditingClientId(false)}
            sx={{
              marginBottom: 2,
              width: "80%",
              opacity: isEditingClientId || clientId === "" ? 1 : 0.5,
              transition: "opacity 0.3s",
            }}
          />

          <TextField
            label="Client Secret"
            variant="outlined"
            type={showPassword ? "text" : "password"}
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            onFocus={() => setIsEditingClientSecret(true)}
            onBlur={() => setIsEditingClientSecret(false)}
            sx={{
              marginBottom: 2,
              width: "80%",
              opacity: isEditingClientSecret || clientSecret === "" ? 1 : 0.5,
              transition: "opacity 0.3s",
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </>
      )}

      <Button
        variant="contained"
        onClick={handleIntegrate}
        disabled={isSubmitting || loading}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "150px",
          bgcolor: colors.blue[200],
          color: "gray",
          boxShadow: "none",
        }}
      >
        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Connect"}
      </Button>

      <Snackbar open={openSnackbar} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={resSeverity} sx={{ width: "100%", zIndex: "999999" }}>
          {resSeverityMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};
